import { createFileRoute, Link } from '@tanstack/react-router'
import { guardJeridSchoolAdminOnly } from '../../lib/auth/routeGuards'
import { useQuery } from '@tanstack/react-query'
import { jeridSchoolAdminService } from '@/lib/api/services/jerid-school-admin-service'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import {
  AlertTriangle,
  Building2,
  CheckCircle2,
  RefreshCw,
  Settings,
  UserCheck,
  Users,
  UserX,
} from 'lucide-react'
import { useUserStore } from '@/store/userStore'

export const Route = createFileRoute('/jerid_school_admin/')({
  beforeLoad: guardJeridSchoolAdminOnly,
  component: DashboardComponent,
})

function DashboardComponent() {
  const user = useUserStore.getState().user
  const dashboardQuery = useQuery({
    queryKey: ['jerid-school-admin-dashboard'],
    queryFn: jeridSchoolAdminService.getDashboard,
  })

  if (dashboardQuery.isLoading) {
    return (
      <div className="space-y-6 p-4 md:p-8">
        <div>
          <Skeleton className="h-8 w-64" />
          <Skeleton className="mt-2 h-4 w-80" />
        </div>
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
          {Array.from({ length: 4 }).map((_, index) => (
            <Skeleton key={index} className="h-32 rounded-md" />
          ))}
        </div>
        <Skeleton className="h-72 rounded-md" />
      </div>
    )
  }

  if (dashboardQuery.isError || !dashboardQuery.data) {
    return (
      <div className="p-4 md:p-8">
        <Card className="border-red-200 bg-red-50">
          <CardContent className="flex flex-col items-start gap-4 p-6 md:flex-row md:items-center md:justify-between">
            <div className="flex gap-3">
              <AlertTriangle className="mt-0.5 h-5 w-5 text-red-600" />
              <div>
                <h1 className="font-semibold text-red-950">
                  Dashboard data could not be loaded
                </h1>
                <p className="mt-1 text-sm text-red-700">
                  The back office is connected to the API, but the summary
                  request failed. Try again or check the backend response.
                </p>
              </div>
            </div>
            <Button
              variant="outline"
              onClick={() => dashboardQuery.refetch()}
              className="gap-2"
            >
              <RefreshCw className="h-4 w-4" />
              Retry
            </Button>
          </CardContent>
        </Card>
      </div>
    )
  }

  const dashboard = dashboardQuery.data
  const stats = [
    {
      title: 'School directors',
      value: dashboard.totals.directors,
      detail: `${dashboard.totals.activeDirectors} active`,
      icon: Users,
      link: '/jerid_school_admin/directors',
    },
    {
      title: 'Active directors',
      value: dashboard.totals.activeDirectors,
      detail: `${dashboard.totals.inactiveDirectors} inactive`,
      icon: UserCheck,
      link: '/jerid_school_admin/directors',
    },
    {
      title: 'Schools',
      value: dashboard.totals.establishments,
      detail: `${dashboard.totals.activeEstablishments} active`,
      icon: Building2,
      link: '/jerid_school_admin/establishments',
    },
    {
      title: 'Services',
      value: dashboard.totals.services,
      detail: `${dashboard.totals.coreServices} core`,
      icon: Settings,
      link: '/jerid_school_admin/services',
    },
  ]

  return (
    <div className="space-y-8 p-4 md:p-8">
      <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-normal text-slate-950 md:text-3xl">
            Platform Dashboard
          </h1>
          <p className="mt-1 text-sm text-slate-500">
            Welcome back{user?.firstname ? `, ${user.firstname}` : ''}. Live
            platform data is shown from the JeridSchool admin API.
          </p>
        </div>
        <Button
          variant="outline"
          onClick={() => dashboardQuery.refetch()}
          className="w-fit gap-2"
        >
          <RefreshCw className="h-4 w-4" />
          Refresh
        </Button>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {stats.map((stat) => (
          <Link key={stat.title} to={stat.link}>
            <Card className="h-full rounded-md border-slate-200 transition-colors hover:border-blue-300 hover:bg-blue-50/30">
              <CardContent className="p-5">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <p className="text-sm font-medium text-slate-500">
                      {stat.title}
                    </p>
                    <p className="mt-3 text-3xl font-semibold text-slate-950">
                      {stat.value}
                    </p>
                    <p className="mt-1 text-xs text-slate-500">{stat.detail}</p>
                  </div>
                  <div className="rounded-md bg-blue-50 p-3 text-blue-700">
                    <stat.icon className="h-5 w-5" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </Link>
        ))}
      </div>

      <div className="grid gap-6 xl:grid-cols-[1.1fr_0.9fr]">
        <Card className="rounded-md border-slate-200">
          <CardHeader>
            <CardTitle className="text-base">Recent schools</CardTitle>
          </CardHeader>
          <CardContent>
            {dashboard.recent.establishments.length ? (
              <div className="divide-y divide-slate-100">
                {dashboard.recent.establishments.map((school) => (
                  <div
                    key={school.id}
                    className="flex flex-col gap-2 py-3 md:flex-row md:items-center md:justify-between"
                  >
                    <div>
                      <p className="font-medium text-slate-950">
                        {school.name}
                      </p>
                      <p className="text-sm text-slate-500">
                        {school.superAdmin
                          ? `${school.superAdmin.firstname} ${school.superAdmin.lastname}`
                          : 'No director assigned'}
                      </p>
                    </div>
                    <Badge variant={school.isActive ? 'default' : 'secondary'}>
                      {school.isActive ? 'Active' : 'Inactive'}
                    </Badge>
                  </div>
                ))}
              </div>
            ) : (
              <EmptyState
                icon={Building2}
                title="No schools yet"
                description="Create an establishment once a school director is available."
              />
            )}
          </CardContent>
        </Card>

        <Card className="rounded-md border-slate-200">
          <CardHeader>
            <CardTitle className="text-base">Directors needing schools</CardTitle>
          </CardHeader>
          <CardContent>
            {dashboard.directorsWithoutSchools.length ? (
              <div className="divide-y divide-slate-100">
                {dashboard.directorsWithoutSchools.slice(0, 5).map((director) => (
                  <div
                    key={director.id}
                    className="flex items-center justify-between gap-3 py-3"
                  >
                    <div>
                      <p className="font-medium text-slate-950">
                        {director.firstname} {director.lastname}
                      </p>
                      <p className="text-sm text-slate-500">{director.email}</p>
                    </div>
                    <UserX className="h-4 w-4 text-amber-600" />
                  </div>
                ))}
              </div>
            ) : (
              <EmptyState
                icon={CheckCircle2}
                title="All directors are assigned"
                description="Every active director currently has an establishment."
              />
            )}
          </CardContent>
        </Card>
      </div>

      <Card className="rounded-md border-slate-200">
        <CardHeader>
          <CardTitle className="text-base">Service adoption</CardTitle>
        </CardHeader>
        <CardContent>
          {dashboard.serviceAdoption.length ? (
            <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
              {dashboard.serviceAdoption.slice(0, 12).map((service) => (
                <div
                  key={service.id}
                  className="rounded-md border border-slate-200 bg-white p-4"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="font-medium text-slate-950">
                        {service.name}
                      </p>
                      <p className="text-xs text-slate-500">
                        {service.category}
                      </p>
                    </div>
                    {service.isCore && (
                      <Badge variant="secondary" className="text-xs">
                        Core
                      </Badge>
                    )}
                  </div>
                  <p className="mt-3 text-sm text-slate-600">
                    Enabled for{' '}
                    <span className="font-semibold text-slate-950">
                      {service.enabledCount}
                    </span>{' '}
                    director(s)
                  </p>
                </div>
              ))}
            </div>
          ) : (
            <EmptyState
              icon={Settings}
              title="No services registered"
              description="Service definitions will appear here once registered."
            />
          )}
        </CardContent>
      </Card>
    </div>
  )
}

function EmptyState({
  icon: Icon,
  title,
  description,
}: {
  icon: typeof Building2
  title: string
  description: string
}) {
  return (
    <div className="flex flex-col items-center justify-center rounded-md border border-dashed border-slate-200 py-10 text-center">
      <Icon className="h-8 w-8 text-slate-300" />
      <p className="mt-3 font-medium text-slate-700">{title}</p>
      <p className="mt-1 max-w-sm text-sm text-slate-500">{description}</p>
    </div>
  )
}
